/**
 * Eventi del tema verso il pixel di Shopify.
 *
 * Shopify pubblica da solo gli eventi di e-commerce (pagina vista, prodotto visto, aggiunta al
 * carrello, checkout, acquisto). Qui si aggiungono quelli che il negozio conosce e Shopify no:
 * clic su una scheda in lista, impressioni delle liste, uso dei filtri, ricerche a vuoto, cambio
 * colore, invio dei moduli e clic sui contatti.
 *
 * Il pixel personalizzato (Impostazioni → Eventi dei clienti) li riceve con analytics.subscribe
 * e li gira a Google Tag Manager. Qui non c'è nulla di Google: se un domani il pixel cambia, il
 * tema resta com'è.
 *
 * Nessun dato personale: solo codice articolo, nome, prezzo e il nome del modulo.
 *
 * Google Tag Manager nella pagina (Impostazioni del tema → Analisi): se c'è l'ID del container,
 * GTM si carica qui invece che in un pixel. Un pixel gira in una cornice nascosta e Google non
 * vi misura il tempo di permanenza; nella pagina vera sì. Il container si carica solo dopo il
 * consenso ad analisi e marketing e riceve le scelte come segnale di consenso (consent mode). Gli
 * eventi e-commerce del negozio partono da qui; quelli del checkout restano al pixel, perché il
 * checkout non esegue il codice del tema.
 */
(() => {
  const pubblica = (nome, dati) => {
    try {
      window.Shopify?.analytics?.publish?.(nome, dati);
    } catch (e) {
      /* il tracciamento non deve mai rompere la pagina */
    }
  };

  // --- Google Tag Manager --------------------------------------------------------
  const ga = window.themeAnalytics;
  // dataLayer con un nome suo: il tag dell'app Google & YouTube usa window.dataLayer e, se lo
  // condividesse, manderebbe a GA4 i suoi eventi in doppio (es. view_item con altri codici articolo)
  const NOME_DL = 'themeDataLayer';
  const dataLayer = (window[NOME_DL] = window[NOME_DL] || []);
  // consent mode vuole l'oggetto arguments, non un array
  function gtag() {
    dataLayer.push(arguments); // eslint-disable-line prefer-rest-params
  }
  /** Evento per GTM; con `ecommerce` si azzera prima il precedente, come chiede GA4. */
  const ga4 = (nome, dati = {}) => {
    if (!ga) return;
    try {
      if (dati.ecommerce) dataLayer.push({ ecommerce: null });
      dataLayer.push({ event: nome, ...dati });
    } catch (e) {
      /* idem */
    }
  };
  const segnali = (analisi, marketing) => ({
    analytics_storage: analisi ? 'granted' : 'denied',
    ad_storage: marketing ? 'granted' : 'denied',
    ad_user_data: marketing ? 'granted' : 'denied',
    ad_personalization: marketing ? 'granted' : 'denied',
  });
  let gtmCaricato = false;
  const aggiornaConsenso = (analisi, marketing) => {
    if (gtmCaricato) {
      gtag('consent', 'update', segnali(analisi, marketing));
      return;
    }
    // come il pixel del checkout: si parte solo con il consenso ad analisi e marketing insieme
    if (!analisi || !marketing) return;
    gtmCaricato = true;
    gtag('consent', 'default', segnali(analisi, marketing));
    dataLayer.push({ 'gtm.start': Date.now(), event: 'gtm.js' });
    const s = document.createElement('script');
    s.async = true;
    s.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(ga.containerId)}&l=${NOME_DL}`;
    document.head.appendChild(s);
  };
  if (ga) {
    const leggiConsenso = () => {
      const cp = window.Shopify?.customerPrivacy;
      if (cp) aggiornaConsenso(cp.analyticsProcessingAllowed?.() === true, cp.marketingAllowed?.() === true);
    };
    document.addEventListener('visitorConsentCollected', (e) =>
      aggiornaConsenso(e.detail?.analyticsAllowed === true, e.detail?.marketingAllowed === true)
    );
    if (window.Shopify?.loadFeatures) {
      window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], (err) => {
        if (!err) leggiConsenso();
      });
    } else {
      leggiConsenso();
    }

    // eventi che dipendono dalla pagina: li prepara snippets/analytics.liquid
    if (ga.item) {
      ga4('view_item', { ecommerce: { currency: ga.currency, value: ga.item.price, items: [ga.item] } });
    }
    if (ga.cart) {
      ga4('view_cart', { ecommerce: { currency: ga.currency, value: ga.cart.value, items: ga.cart.items } });
    }
  }

  /** Riga del carrello (risposte di /cart/add.js e /cart/change.js) → articolo GA4. */
  const daRiga = (r, quantita) => ({
    item_id: r.sku || String(r.variant_id || ''),
    item_name: r.product_title || r.title || '',
    item_variant: r.variant_title || '',
    item_brand: r.vendor || '',
    item_category: r.product_type || '',
    price: (r.final_price ?? r.price ?? 0) / 100,
    quantity: quantita ?? r.quantity ?? 1,
  });
  const carrello = (nome, righe) => {
    if (!righe.length) return;
    const valore = righe.reduce((t, i) => t + i.price * i.quantity, 0);
    ga4(nome, { ecommerce: { currency: ga?.currency, value: Math.round(valore * 100) / 100, items: righe } });
  };
  document.addEventListener('theme:cart:add', (e) => {
    const d = e.detail || {};
    carrello('add_to_cart', (d.items || [d]).filter((r) => r && r.variant_id).map((r) => daRiga(r)));
  });
  document.addEventListener('theme:cart:change', (e) => {
    const d = e.detail || {};
    carrello('add_to_cart', (d.items_added || []).map((r) => daRiga(r)));
    carrello('remove_from_cart', (d.items_removed || []).map((r) => daRiga(r)));
  });

  /** Scheda in lista (data-track-*) → articolo GA4. */
  const articolo = (p) => ({
    item_id: p.sku,
    item_name: p.title,
    item_brand: p.vendor,
    item_category: p.type,
    item_variant: p.variantTitle,
    price: p.price,
    index: p.position,
    quantity: 1,
  });

  /** Legge i data-track-* di una scheda prodotto. */
  const daScheda = (card, posizione) => ({
    sku: card.dataset.trackId || '',
    title: card.dataset.trackName || '',
    vendor: card.dataset.trackBrand || '',
    type: card.dataset.trackCategory || '',
    variantTitle: card.dataset.trackVariant || '',
    price: parseFloat(card.dataset.trackPrice || '0') || 0,
    position: posizione,
  });

  /** Nome della lista in cui si trova la scheda: collezione, ricerca, sezione della home. */
  const nomeLista = (card) => {
    const sezione = card.closest('[data-track-list]');
    if (sezione) return sezione.dataset.trackList;
    const b = document.body;
    if (b.classList.contains('template-collection')) return document.title;
    if (b.classList.contains('template-search')) return 'Ricerca';
    return 'Lista prodotti';
  };

  const indiceIn = (card) => {
    const lista = card.closest('[data-track-list]') || document;
    return [...lista.querySelectorAll('[data-product-card]')].indexOf(card) + 1;
  };

  // --- clic su una scheda in lista ------------------------------------------
  document.addEventListener('click', (e) => {
    const card = e.target.closest('[data-product-card]');
    if (!card) return;
    // il clic sullo swatch cambia solo la foto, non porta alla scheda
    if (e.target.closest('.product-card__swatch')) return;
    if (!e.target.closest('a, button')) return;
    const scheda = daScheda(card, indiceIn(card));
    pubblica('ztech_product_clicked', { ...scheda, listName: nomeLista(card) });
    ga4('select_item', { ecommerce: { item_list_name: nomeLista(card), items: [articolo(scheda)] } });
  });

  // --- impressioni delle liste ----------------------------------------------
  // Si raccolgono le schede viste e si manda un evento solo per lista, mezzo secondo dopo
  // l'ultima comparsa: una collezione lunga non genera decine di eventi.
  const viste = new Map();
  let attesa = null;
  const svuota = () => {
    viste.forEach((prodotti, lista) => {
      if (!prodotti.length) return;
      pubblica('ztech_product_list_viewed', { listName: lista, items: prodotti });
      ga4('view_item_list', { ecommerce: { item_list_name: lista, items: prodotti.map(articolo) } });
    });
    viste.clear();
  };
  const osservatore =
    'IntersectionObserver' in window
      ? new IntersectionObserver(
          (voci) => {
            voci.forEach((v) => {
              if (!v.isIntersecting) return;
              osservatore.unobserve(v.target);
              const lista = nomeLista(v.target);
              if (!viste.has(lista)) viste.set(lista, []);
              viste.get(lista).push(daScheda(v.target, indiceIn(v.target)));
            });
            clearTimeout(attesa);
            attesa = setTimeout(svuota, 500);
          },
          { threshold: 0.5 }
        )
      : null;
  const osserva = () => {
    if (!osservatore) return;
    document.querySelectorAll('[data-product-card]:not([data-track-seen])').forEach((c) => {
      c.setAttribute('data-track-seen', '');
      osservatore.observe(c);
    });
  };
  osserva();
  // Le schede possono arrivare dopo il caricamento: filtri, «Carica altri», «Visti di recente»,
  // anteprima rapida. Invece di agganciare un evento per ogni funzione, si guarda quando nuove
  // schede entrano nella pagina; osserva() prende solo quelle non ancora viste.
  document.addEventListener('theme:facets:updated', osserva);
  if ('MutationObserver' in window) {
    let rinvio = null;
    new MutationObserver((cambi) => {
      if (!cambi.some((c) => c.addedNodes.length)) return;
      clearTimeout(rinvio);
      rinvio = setTimeout(osserva, 200);
    }).observe(document.body, { childList: true, subtree: true });
  }

  // --- filtri di categoria ---------------------------------------------------
  document.addEventListener('change', (e) => {
    const campo = e.target.closest('[data-facets-form] input, [data-facets-form] select');
    if (!campo) return;
    const filtro = {
      filterName: campo.name || '',
      filterValue: campo.type === 'checkbox' ? campo.value : campo.value || '',
      active: campo.type === 'checkbox' ? campo.checked : true,
    };
    pubblica('ztech_filter_used', filtro);
    ga4('ztech_filter_used', { filter_name: filtro.filterName, filter_value: filtro.filterValue, filter_active: filtro.active });
  });

  // --- ricerca senza risultati ------------------------------------------------
  // Dice quali prodotti la gente cerca e non trova: è la lista della spesa del catalogo.
  if (document.body.classList.contains('template-search')) {
    const risultati = document.querySelector('[data-search-count]');
    const quanti = risultati ? parseInt(risultati.dataset.searchCount, 10) : NaN;
    const chiave = new URLSearchParams(location.search).get('q');
    if (chiave && quanti === 0) {
      pubblica('ztech_search_no_results', { query: chiave });
      ga4('ztech_search_no_results', { search_term: chiave });
    }
  }

  // --- cambio colore sulla scheda prodotto -------------------------------------
  document.addEventListener('theme:variant:change', (e) => {
    const v = e.detail?.variant;
    if (!v) return;
    // conta solo la scheda prodotto vera: il cambio dall'anteprima rapida (su una collezione)
    // avrebbe come titolo quello della collezione e gonfierebbe il conteggio
    const pagina = e.detail?.page;
    if (pagina && !pagina.classList?.contains('product-main')) return;
    const variante = {
      sku: v.sku || '',
      title: document.title,
      variantTitle: v.title || '',
      price: (v.price || 0) / 100,
      available: v.available === true,
    };
    pubblica('ztech_variant_changed', variante);
    ga4('ztech_variant_changed', {
      item_id: variante.sku,
      item_name: ga?.item?.item_name || variante.title,
      item_variant: variante.variantTitle,
      price: variante.price,
      in_stock: variante.available,
    });
  });

  // --- invio dei moduli --------------------------------------------------------
  const nomeModulo = (form) => {
    if (form.dataset.trackForm) return form.dataset.trackForm;
    if (form.hasAttribute('data-back-in-stock-form')) return 'avvisami';
    if (form.hasAttribute('data-rivenditore-form')) return 'richiesta-rivenditore';
    const tipo = form.querySelector('input[name="form_type"]')?.value;
    if (tipo === 'contact') return 'contatti';
    if (tipo === 'customer') return 'cliente';
    return '';
  };
  document.addEventListener('submit', (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    if (form.hasAttribute('data-ajax-cart')) return; // già coperto da Shopify
    const nome = nomeModulo(form);
    if (!nome) return;
    pubblica('ztech_form_submitted', { formType: nome, pagePath: location.pathname });
    ga4('generate_lead', { form_type: nome, form_location: location.pathname });
  });

  // --- clic sui contatti ---------------------------------------------------------
  // Su veicoli di questo prezzo molti preferiscono telefonare: senza questo non si vede.
  document.addEventListener('click', (e) => {
    const link = e.target.closest('a[href^="tel:"], a[href^="mailto:"], a[href*="wa.me"], a[href*="api.whatsapp.com"]');
    if (!link) return;
    const href = link.getAttribute('href') || '';
    let canale = 'email';
    if (href.startsWith('tel:')) canale = 'telefono';
    else if (href.includes('wa.me') || href.includes('whatsapp')) canale = 'whatsapp';
    pubblica('ztech_contact_click', { channel: canale, pagePath: location.pathname });
    ga4('ztech_contact_click', { contact_channel: canale, form_location: location.pathname });
  });
})();
